import React, { useState, useEffect, Suspense } from 'react';
import { IconSpinner } from '../../components';
import css from './AdminSocialReviewerPage.css';

/**
 * ReviewerClientOnly — the SSR-safe boundary for the animated reviewer subtree.
 *
 * Mirrors the workflow editor's client-only guard: the repo's SSR is real
 * (server/renderer.js renderToString) and the reviewer uses Motion's `layout` /
 * AnimatePresence (which reads the DOM) plus react-konva design editing — none
 * of which may run on the server. We render a placeholder during SSR + the first
 * synchronous client render, then flip to the lazily-loaded reviewer once mounted
 * in the browser. This same guard covers the dev REACT_APP_DEV_FAKE_ADMIN bypass
 * (where there is no server auth redirect to keep the heavy tree off SSR).
 */
const LazyReviewer = React.lazy(() => import('./ReviewerInner'));

const Placeholder = () => (
    <div className={css.bootPlaceholder}>
        <IconSpinner />
    </div>
);

/**
 * Contain a render error inside the reviewer so a single bad post/frame never
 * blanks the whole admin surface. Shows the error so it is actionable.
 */
class ReviewerErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('AdminSocialReviewer crashed:', error, info && info.componentStack);
    }
    render() {
        if (this.state.error) {
            return (
                <div className={css.bootPlaceholder} role="alert">
                    <p className={css.bootError}>Der Reviewer konnte nicht geladen werden.</p>
                    <pre className={css.bootErrorDetail}>
                        {String(this.state.error && this.state.error.message)}
                    </pre>
                </div>
            );
        }
        return this.props.children;
    }
}

const ReviewerClientOnly = props => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return <Placeholder />;

    return (
        <ReviewerErrorBoundary>
            <Suspense fallback={<Placeholder />}>
                <LazyReviewer {...props} />
            </Suspense>
        </ReviewerErrorBoundary>
    );
};

export default ReviewerClientOnly;
